import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  useCreateStockTransferMutation,
  useGetLocationsQuery,
  useGetStockLevelsQuery,
  useLazyGetProductBatchesQuery
} from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const round2 = (num) => Math.round((Number(num || 0) + Number.EPSILON) * 100) / 100;

const calculateFIFOCost = (batches, quantity, defaultCost) => {
  let remaining = parseFloat(quantity) || 0;
  let totalCost = 0;

  if (batches && batches.length > 0) {
    // Sort batches by receivedDate and createdAt ascending for FIFO order
    const sortedBatches = [...batches].sort((a, b) => {
      const dateA = new Date(a.receivedDate || a.createdAt);
      const dateB = new Date(b.receivedDate || b.createdAt);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    for (const batch of sortedBatches) {
      if (remaining <= 0) break;
      const available = parseFloat(batch.quantity) || 0;
      if (available <= 0) continue;

      const take = Math.min(available, remaining);
      totalCost += take * parseFloat(batch.unitCost || 0);
      remaining -= take;
    }
  }

  // If there is still quantity remaining (or no batches found), fall back to defaultCost
  if (remaining > 0) {
    totalCost += remaining * (defaultCost || 0);
  }

  const total = round2(totalCost);
  const unit = quantity > 0 ? round2(totalCost / quantity) : defaultCost;

  return { unitCost: unit, totalCost: total };
};

function StockTransferForm() {
  const navigate = useNavigate();
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([
    { productId: '', quantity: 1, productName: '', unitCost: 0, totalCost: 0, batchId: '', batchNo: '', batches: [] }
  ]);

  const { data: locations } = useGetLocationsQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: company } = useGetCurrentCompanyQuery();
  const [createTransfer, { isLoading: isSaving }] = useCreateStockTransferMutation();
  const [triggerFetchBatches] = useLazyGetProductBatchesQuery();

  const currency = company?.currency || 'USD';

  // Get stock levels for fromLocation to validate/show available qty
  const { data: stockLevelsData } = useGetStockLevelsQuery(
    { locationId: fromLocationId, limit: 1000 },
    { skip: !fromLocationId }
  );

  const products = productsData?.products || [];
  const stockLevels = stockLevelsData?.stockLevels || [];

  const totalTransferAmount = round2(items.reduce((sum, item) => sum + (Number(item.totalCost) || 0), 0));
  const totalTransferQuantity = round2(items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0));

  // Automatically update unitCost and batches when source location changes
  useEffect(() => {
    const fetchAllBatches = async () => {
      if (!fromLocationId || items.length === 0) return;

      const updatedItems = await Promise.all(
        items.map(async (item) => {
          if (!item.productId) return item;

          try {
            const batches = await triggerFetchBatches({
              productId: item.productId,
              locationId: fromLocationId,
            }).unwrap();

            const product = products.find((p) => p.id === item.productId);
            const factor = parseFloat(product?.conversionFactor) || 1;
            const defaultCost = (product?.costPrice || 0) / factor;

            let finalUnitCost = item.unitCost;
            let finalTotalCost = item.totalCost;
            let finalBatchId = item.batchId;
            let finalBatchNo = item.batchNo;

            if (item.batchId) {
              const matchedBatch = (batches || []).find((b) => b.id === item.batchId);
              if (matchedBatch) {
                finalUnitCost = parseFloat(matchedBatch.unitCost) || 0;
                finalTotalCost = round2((item.quantity || 0) * finalUnitCost);
              } else {
                finalBatchId = '';
                finalBatchNo = '';
                const { unitCost, totalCost } = calculateFIFOCost(batches, item.quantity, defaultCost);
                finalUnitCost = unitCost;
                finalTotalCost = totalCost;
              }
            } else {
              const { unitCost, totalCost } = calculateFIFOCost(batches, item.quantity, defaultCost);
              finalUnitCost = unitCost;
              finalTotalCost = totalCost;
            }

            return {
              ...item,
              batches: batches || [],
              batchId: finalBatchId,
              batchNo: finalBatchNo,
              defaultCost,
              unitCost: finalUnitCost,
              totalCost: finalTotalCost,
            };
          } catch (err) {
            console.error('Error fetching batches for location change:', err);
            return item;
          }
        })
      );

      setItems(updatedItems);
    };

    fetchAllBatches();
  }, [fromLocationId]);

  const handleAddItem = () => {
    setItems([
      ...items,
      { productId: '', quantity: 1, productName: '', unitCost: 0, totalCost: 0, batchId: '', batchNo: '', batches: [] }
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...items];
    const item = { ...newItems[index] };

    if (field === 'product') {
      const productId = value?.id || '';
      item.productId = productId;
      item.productName = value?.name || '';
      item.batches = [];
      item.batchId = '';
      item.batchNo = '';

      const factor = parseFloat(value?.conversionFactor) || 1;
      const defaultCost = (value?.costPrice || 0) / factor;
      item.defaultCost = defaultCost;

      if (productId && fromLocationId) {
        try {
          const batches = await triggerFetchBatches({ productId, locationId: fromLocationId }).unwrap();
          item.batches = batches || [];
          const { unitCost, totalCost } = calculateFIFOCost(item.batches, item.quantity || 1, defaultCost);
          item.unitCost = unitCost;
          item.totalCost = totalCost;
        } catch (err) {
          console.error('Failed to fetch product batches:', err);
          item.unitCost = defaultCost;
          item.totalCost = round2((item.quantity || 1) * defaultCost);
        }
      } else {
        item.unitCost = defaultCost;
        item.totalCost = round2((item.quantity || 1) * defaultCost);
      }
    } else if (field === 'batch') {
      const selectedBatch = value; // value is a batch object or null
      item.batchId = selectedBatch?.id || '';
      item.batchNo = selectedBatch?.batchNo || '';

      if (selectedBatch) {
        item.unitCost = parseFloat(selectedBatch.unitCost) || 0;
        const batchQty = parseFloat(selectedBatch.quantity) || 0;
        if (item.quantity > batchQty) {
          item.quantity = batchQty;
        }
        item.totalCost = round2(item.quantity * item.unitCost);
      } else {
        const { unitCost, totalCost } = calculateFIFOCost(
          item.batches,
          item.quantity,
          item.defaultCost || 0
        );
        item.unitCost = unitCost;
        item.totalCost = totalCost;
      }
    } else if (field === 'quantity') {
      const qty = parseFloat(value) || 0;
      item.quantity = qty;

      if (item.batchId) {
        const matchedBatch = (item.batches || []).find((b) => b.id === item.batchId);
        if (matchedBatch) {
          const batchQty = parseFloat(matchedBatch.quantity) || 0;
          if (qty > batchQty) {
            item.quantity = batchQty;
          }
        }
        item.totalCost = round2(item.quantity * (item.unitCost || 0));
      } else {
        const { unitCost, totalCost } = calculateFIFOCost(
          item.batches,
          qty,
          item.defaultCost || 0
        );
        item.unitCost = unitCost;
        item.totalCost = totalCost;
      }
    } else if (field === 'unitCost') {
      const cost = parseFloat(value) || 0;
      item.unitCost = cost;
      item.totalCost = round2((item.quantity || 0) * cost);
    } else {
      item[field] = value;
    }

    newItems[index] = item;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromLocationId || !toLocationId) {
      alert('Please select both source and destination locations.');
      return;
    }
    if (fromLocationId === toLocationId) {
      alert('Source and destination locations must be different.');
      return;
    }
    if (items.some((item) => !item.productId || item.quantity <= 0)) {
      alert('Please select products and valid quantities for all items.');
      return;
    }

    try {
      const res = await createTransfer({
        fromLocationId,
        toLocationId,
        transferDate,
        notes,
        totalAmount: totalTransferAmount,
        items: items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          unitCost: i.unitCost,
          totalCost: i.totalCost,
          batchId: i.batchId || undefined,
          batchNo: i.batchNo || undefined,
        })),
      }).unwrap();

      alert(`Stock transfer ${res?.stockTransfer?.transferNo || ''} completed successfully!`);
      navigate('/inventory/transfers');
    } catch (error) {
      alert(error?.data?.message || 'Error creating stock transfer');
    }
  };

  const formatAmount = (val) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: company?.currencyDecimals ?? 2,
    }).format(Number(val || 0));
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/inventory/transfers')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          New Stock Transfer
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="From Location (Source)"
                value={fromLocationId}
                onChange={(e) => setFromLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="To Location (Destination)"
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id} disabled={loc.id === fromLocationId}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                type="date"
                label="Transfer Date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes or reference..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Items to Transfer</Typography>
            <Typography variant="body2" color="text.secondary">
              Select specific batches or transfer using FIFO auto-allocation
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Batch</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Unit</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 130 }}>Available</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 130 }}>Unit Cost</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 120 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 140 }} align="right">Total Cost</TableCell>
                  <TableCell width={50} align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const stock = stockLevels.find((s) => s.productId === item.productId);
                  const totalAvailable = stock ? parseFloat(stock.quantity) - parseFloat(stock.reservedQuantity) : 0;

                  const selectedBatchObj = (item.batches || []).find((b) => b.id === item.batchId);
                  const batchAvailable = selectedBatchObj ? parseFloat(selectedBatchObj.quantity) : totalAvailable;
                  const hasBatches = (item.batches || []).length > 0;
                  const isUnitCostEditable = !item.batchId && !hasBatches;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.name} (${option.sku})`}
                          value={products.find((p) => p.id === item.productId) || null}
                          onChange={(_, newValue) => handleItemChange(index, 'product', newValue)}
                          renderInput={(params) => <TextField {...params} label="Select Product" required size="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={item.batches || []}
                          getOptionLabel={(option) =>
                            option.batchNo
                              ? `${option.batchNo} (Avail: ${option.quantity}, Cost: ${option.unitCost})`
                              : `Batch ${option.id?.substring(0, 6)} (Avail: ${option.quantity}, Cost: ${option.unitCost})`
                          }
                          value={selectedBatchObj || null}
                          onChange={(_, newValue) => handleItemChange(index, 'batch', newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={hasBatches ? "Select Batch (or FIFO Auto)" : "No Batches (Default)"}
                              size="small"
                            />
                          )}
                          disabled={!item.productId || !hasBatches}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {products.find((p) => p.id === item.productId)?.baseUnit || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            color={batchAvailable <= 0 ? 'error.main' : 'text.primary'}
                          >
                            {batchAvailable}
                          </Typography>
                          {item.batchId ? (
                            <Typography variant="caption" color="text.secondary">
                              Batch stock
                            </Typography>
                          ) : hasBatches ? (
                            <Typography variant="caption" color="primary.main">
                              Total across {item.batches.length} batch(es)
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          value={item.unitCost}
                          onChange={(e) => handleItemChange(index, 'unitCost', parseFloat(e.target.value))}
                          inputProps={{ min: 0, step: 0.01, readOnly: !isUnitCostEditable }}
                          helperText={item.batchId ? "Batch cost" : hasBatches ? "FIFO cost" : "Default cost"}
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                          inputProps={{ min: 0.01, step: 0.01, max: item.batchId ? batchAvailable : undefined }}
                          required
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatAmount(item.totalCost)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleRemoveItem(index)} disabled={items.length === 1}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              variant="outlined"
              size="small"
            >
              Add Another Item / Batch
            </Button>
            <Box sx={{ display: 'flex', gap: 4, textAlign: 'right' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Items</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {items.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Quantity</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalTransferQuantity}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Transfer Value</Typography>
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                  {formatAmount(totalTransferAmount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/inventory/transfers')}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? 'Processing Transfer...' : 'Complete Transfer'}
        </Button>
      </Box>
    </Box>
  );
}

export default StockTransferForm;
